import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AdminLayout from '../../components/AdminLayout';
import { siteApi, SiteSettings as SiteSettingsData, StatePageVisibility } from '../../api/site';

const STATE_PAGE_VISIBILITY_OPTIONS: { value: StatePageVisibility; label: string }[] = [
  { value: 'none', label: 'No states — nothing gets a page' },
  { value: 'sgo_registered', label: 'States where you\'re a registered SGO' },
  { value: 'opted_in', label: 'States where you operate' },
  { value: 'all', label: 'Every state' },
];

const SiteSettings: React.FC = () => {
  const queryClient = useQueryClient();

  const { data: settings } = useQuery<SiteSettingsData>({
    queryKey: ['site-settings'],
    queryFn: siteApi.getSettings,
  });

  const [fullSite, setFullSite] = useState(false);
  const [statePageVisibility, setStatePageVisibility] = useState<StatePageVisibility>('none');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings) {
      setFullSite(settings.full_site_enabled);
      setStatePageVisibility(settings.state_page_visibility);
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: (value: boolean) => siteApi.updateSettings({ full_site_enabled: value }),
    onSuccess: (data) => {
      queryClient.setQueryData(['site-settings'], data);
      // The public branding query drives RootRedirect; refresh it so the toggle
      // takes effect without a reload.
      queryClient.invalidateQueries({ queryKey: ['tenant_branding'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  const onToggle = (value: boolean) => {
    setFullSite(value);
    saveMutation.mutate(value);
  };

  const [savedVisibility, setSavedVisibility] = useState(false);

  const visibilityMutation = useMutation({
    mutationFn: (value: StatePageVisibility) =>
      siteApi.updateSettings({ state_page_visibility: value }),
    onSuccess: (data) => {
      queryClient.setQueryData(['site-settings'], data);
      setSavedVisibility(true);
      setTimeout(() => setSavedVisibility(false), 2500);
    },
  });

  const onVisibilityChange = (value: StatePageVisibility) => {
    setStatePageVisibility(value);
    visibilityMutation.mutate(value);
  };

  return (
    <AdminLayout title="Site">
      {/* Full-site toggle */}
      <section className="sgk-panel">
        <h2 className="sgk-panel__title">Public site</h2>
        <p className="sgk-panel__hint">
          When on, your subdomain serves your full public site. When off,
          visitors only reach your donate and apply pages — you can still see
          and edit the full site yourself.
        </p>
        <div className="sgk-switchrow">
          <label className="sgk-toggle">
            <input
              type="checkbox"
              checked={fullSite}
              onChange={(e) => onToggle(e.target.checked)}
              data-testid="site-full-site-toggle"
            />
            <span className="sgk-toggle__track" />
          </label>
          <span className="sgk-switchrow__text">
            Turn on the full public site
          </span>
          {saved && <span className="sgk-saved" data-testid="site-saved-message">Saved</span>}
        </div>
      </section>

      {/* State pages */}
      <section className="sgk-panel">
        <h2 className="sgk-panel__title">State pages</h2>
        <p className="sgk-panel__hint">
          Choose which states get a public page (at /pages/state/&lt;abbr&gt;/,
          linked from your coverage map). States outside this choice aren't
          linked and don't have a page to visit.
        </p>
        <div className="sgk-field">
          <label htmlFor="state-page-visibility-select">Which states get a page</label>
          <select
            id="state-page-visibility-select"
            className="sgk-input"
            value={statePageVisibility}
            onChange={(e) => onVisibilityChange(e.target.value as StatePageVisibility)}
            data-testid="site-state-page-visibility-select"
          >
            {STATE_PAGE_VISIBILITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {savedVisibility && <span className="sgk-saved" data-testid="site-state-visibility-saved-message">Saved</span>}
        </div>
      </section>

      {/* Pages & menus */}
      <section className="sgk-panel">
        <h2 className="sgk-panel__title">Pages &amp; menus</h2>
        <p className="sgk-panel__hint">
          Edit pages, menus, and theme from the toolbar that's always there
          while you browse your site as an admin.
        </p>
        <a
          href="/"
          className="sgk-btn sgk-btn--navy sgk-btn--sm"
          data-testid="site-visit-homepage-link"
        >
          Go to your site
        </a>
      </section>
    </AdminLayout>
  );
};

export default SiteSettings;
